import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Intl } from '../../../../../constants';
import { ContactSelectDialog, ContactValuePanel } from '@fx-ui/platform-sdk';
import { ContactsFooter } from '../../../../../components';
import {
    ContactValue,
    DeptStatus,
    DeptType,
    MemberStatus,
    MemberType,
    RoleStatus,
    RoleType,
    transformContactValue
} from '@fx-ui/jdy-design-biz';
import { get, isEmpty, map, EMPTY_FN, find } from '@libs/shared';
import { globalState } from '../../../../../models';

interface PluginMemberValue {
    departments: string[];
    roles: string[];
    users: string[];
}

interface PluginMemberSelectProps {
    style?: React.CSSProperties;
    valuePanelClassName?: string;
    disabled?: boolean;
    defaultValue?: PluginMemberValue;
    onOk?: (value?: PluginMemberValue) => void;
}

const PluginMemberSelect: React.FC<PluginMemberSelectProps> = (props) => {
    const { style, valuePanelClassName, disabled, defaultValue, onOk = EMPTY_FN } = props;
    const isExternalUserAvailable = globalState.authModel?.isExternalUserAvailable;

    const [dialogVisible, setDialogVisible] = useState(false);
    const [value, setValue] = useState<ContactValue>();

    useEffect(() => {
        if (isEmpty(defaultValue)) {
            setValue(undefined);
            return;
        }

        const memberIds: string[] = get(defaultValue, 'users', []);
        const roleIds: string[] = get(defaultValue, 'roles', []);
        const deptIds: string[] = get(defaultValue, 'departments', []);

        globalState.request
            ?.onResolveContactDetails(memberIds.concat(roleIds, deptIds))
            .then((data) => {
                const contactValue = {
                    members: map(memberIds, (_id) => {
                        const result = find(data.members, {
                            _id
                        });
                        if (result) {
                            return result;
                        }
                        return {
                            _id,
                            type: MemberType.internal,
                            status: MemberStatus.deleted
                        } as any;
                    }),
                    departments: map(deptIds, (_id) => {
                        const result = find(data.departments, {
                            _id
                        });
                        if (result) {
                            return result;
                        }
                        return {
                            _id,
                            type: DeptType.internal,
                            status: DeptStatus.dismission
                        } as any;
                    }),
                    roles: map(roleIds, (_id) => {
                        const result = find(data.roles, {
                            _id
                        });
                        if (result) {
                            return result;
                        }
                        return {
                            _id,
                            type: RoleType.internal,
                            status: RoleStatus.dismission
                        } as any;
                    })
                };
                setValue(contactValue);
            });
    }, [defaultValue]);

    return (
        <div className="fx-plugin-member-select" style={style}>
            <ContactValuePanel
                className={classNames('member-value-panel', valuePanelClassName)}
                items={transformContactValue(value)}
                tag={{
                    gap: 2
                }}
                disabled={disabled}
                onClick={() => setDialogVisible(true)}
            />
            <ContactSelectDialog
                visible={dialogVisible}
                title={Intl.get('member.list')}
                value={value}
                onCancel={() => setDialogVisible(false)}
                onOk={(value) => {
                    onOk({
                        departments: map(value?.departments, '_id'),
                        roles: map(value?.roles, '_id'),
                        users: map(value?.members, '_id')
                    });
                    setDialogVisible(false);
                }}
                footer={<ContactsFooter />}
                onFetchInternalMembers={globalState.request?.onFetchMembers}
                onFetchExternalMembers={
                    isExternalUserAvailable
                        ? globalState.request?.onFetchExternalMembers
                        : undefined
                }
                onSearchMembers={
                    isExternalUserAvailable
                        ? globalState.request?.onSearchMembers
                        : globalState.request?.onFetchMembers
                }
                onFetchInternalDepartments={globalState.request?.onFetchDepartments}
                onFetchInternalRoles={globalState.request?.onFetchRoles}
                onFetchExternalRoles={
                    isExternalUserAvailable ? globalState.request?.onFetchExternalRoles : undefined
                }
                onSearchRoles={
                    isExternalUserAvailable
                        ? globalState.request?.onSearchRoles
                        : globalState.request?.onFetchRoles
                }
            />
        </div>
    );
};

export default PluginMemberSelect;
